import { api } from '@/lib/api-client'
import type { IssueDerivedStatus, IssueSeverity, IssueStatus } from '@/shared/constants/issue-status'

// ── Types ──────────────────────────────────────────────────────────────────

export interface SiteStats {
  surveys_scheduled: number
  surveys_completed: number
  surveys_missed: number
  issues_active: number
  issues_resolved: number
  survey_progress_percent: number
}

export interface SiteSurveyItem {
  id: string
  survey_ref: string
  status: 'pending' | 'in_progress' | 'completed' | 'missed'
  survey_date?: string | null
  due_date?: string | null
  completed_at?: string | null
  officer_name?: string
  officer?: { id: string; name: string }
  assigned_officer?: { id: string; display_name: string }
  issues_count: number
  has_issues?: boolean
  survey_photos?: string[]
}

// Site-issue rows use the same lifecycle as every other issue endpoint —
// pull the type from the central constant module so the lifecycle
// enum is consistent across all surfaces.
export interface SiteIssueItem {
  id: string
  title: string
  severity: IssueSeverity
  status: IssueStatus
  created_at: string
  survey_ref?: string
  section_title?: string
}

export interface SiteInfo {
  id: string
  site_name: string
  name?: string
  address: string
  contact_number?: string
  site_image_url?: string | null
  lat?: number | null
  long?: number | null
  last_survey_date?: string | null
  entity_id: string
  entity_name: string
  assigned_officer?: { id: string; display_name: string; contact_number?: string } | null
  // Department column. See EntityDetailSite for the same caveat —
  // raw FK UUID the FE uses to preselect the picker.
  department_id?: string | null
}

// Flattened shape used in the component
export interface SiteDetail extends SiteInfo {
  stats: SiteStats
  surveys: { items: SiteSurveyItem[]; total_count: number; page: number }
  issues: { items: SiteIssueItem[]; total_count: number; page: number }
}

interface ApiSiteDetailResponse {
  site: SiteInfo
  assigned_officer?: { id: string; display_name: string; contact_number?: string } | null
  last_survey_date?: string | null
  stats: SiteStats
  surveys: { items: SiteSurveyItem[]; total_count: number; page: number; pagination?: { total_count: number; total_pages: number; current_page: number; has_next: boolean; has_previous: boolean } }
  issues: { items: SiteIssueItem[]; total_count: number; page: number; pagination?: { total_count: number; total_pages: number; current_page: number; has_next: boolean; has_previous: boolean } }
}

export interface SiteDetailParams {
  month?: string
  date_from?: string
  date_to?: string
  surveys_page?: number
  surveys_has_issues?: boolean
  surveys_date_from?: string
  surveys_date_to?: string
  issues_page?: number
  /** DERIVED status (v2): open | pending | overdue | completed | completed-overdue. */
  issues_status?: IssueDerivedStatus
  issues_severity?: 'critical' | 'non_critical'
  issues_date_from?: string
  issues_date_to?: string
}

// ── API functions ───────────────────────────────────────────────────────────

export function getSiteDetail(siteId: string, params?: SiteDetailParams): Promise<SiteDetail> {
  return api
    .get<ApiSiteDetailResponse | SiteDetail>(`/sites/${siteId}/details`, { params })
    .then((d) => {
      // Handle wrapped response: { site: {...}, stats, surveys, issues }
      if ('site' in d && d.site) {
        const raw = d.site as unknown as Record<string, unknown>
        return {
          id: raw['id'] as string,
          site_name: (raw['site_name'] ?? raw['name'] ?? '') as string,
          name: (raw['name'] ?? raw['site_name'] ?? '') as string,
          address: (raw['address'] ?? '') as string,
          contact_number: raw['contact_number'] as string | undefined,
          site_image_url: (raw['site_image_url'] ?? null) as string | null | undefined,
          lat: raw['lat'] as number | undefined,
          long: raw['long'] as number | undefined,
          entity_id: raw['entity_id'] as string,
          entity_name: raw['entity_name'] as string,
          assigned_officer: (d.assigned_officer ?? null),
          last_survey_date: (d.last_survey_date ?? null),
          stats: d.stats,
          surveys: d.surveys,
          issues: d.issues,
        } as unknown as SiteDetail
      }
      return d as SiteDetail
    })
}

// Per-site issues feed for the Issues tab. Inlined here (rather than importing
// from the `issues` system) so this shared feature has no cross-system deps.
export interface SiteIssuesQuery {
  /** DERIVED status (v2): open | pending | overdue | completed | completed-overdue. */
  status?: IssueDerivedStatus
  severity?: IssueSeverity
  date_from?: string
  date_to?: string
  page?: number
}

/**
 * v2: `GET /sites/{id}/issues` was removed — the sanctioned per-site issues
 * read is the `issues` arm of `GET /sites/{id}/details` (issues_* params).
 * Returns the arm re-shaped to the `{ issues, pagination }` the tab expects.
 */
export function getSiteIssues(siteId: string, params?: SiteIssuesQuery): Promise<any> {
  return api
    .get<any>(`/sites/${siteId}/details`, {
      params: {
        issues_status: params?.status,
        issues_severity: params?.severity,
        issues_date_from: params?.date_from,
        issues_date_to: params?.date_to,
        issues_page: params?.page,
      },
    })
    .then((d) => ({ issues: d?.issues?.items ?? [], pagination: d?.issues?.pagination }))
}

export function updateFieldData(siteId: string, data: any) {
  return api.patch<any>(`/sites/${siteId}/field-data`, data)
}

// ── Update / Deactivate ─────────────────────────────────────────────────────

export interface SiteUpdateRequest {
  name?: string
  address?: string | null
  mandal?: string | null
  hostel_type?: string | null
  contact_number?: string | null
  geofence_radius_meters?: number
  // Pass `department_id` to reassign the department. The legacy free-text
  // `department` field is now rejected by the backend with HTTP 400 —
  // never include it.
  department_id?: string
}

export interface SiteUpdated {
  id: string
  name: string
  address: string | null
  mandal: string | null
  hostel_type: string | null
  contact_number: string | null
  geofence_radius_meters: number
  is_active: boolean
  department_id?: string | null
}

export function updateSite(siteId: string, patch: SiteUpdateRequest): Promise<SiteUpdated> {
  return api.patch<SiteUpdated>(`/sites/${siteId}`, patch, {
    successMessage: 'Site updated successfully!',
    suppressErrorToast: true,
  })
}

export interface SiteDeactivated {
  id: string
  name: string
  is_active: false
}

export interface SiteDeactivateBlocked {
  blocking_assignments: number
}

export function deactivateSite(siteId: string): Promise<SiteDeactivated> {
  return api.post<SiteDeactivated>(`/sites/${siteId}/deactivate`, undefined, {
    successMessage: 'Site deactivated.',
    suppressErrorToast: true,
  })
}

// ── Reset field data (admin) ───────────────────────────────────────────────
// Clears the field-officer-captured lat + long + site_image_url together
// (no partial reset), so the officer's mobile flow accepts a fresh capture.
// Idempotent — 200 even when the fields were already empty. The old photo
// stays in S3; only the DB reference is cleared.

export interface SiteFieldDataReset {
  id: string
  name: string
  lat: null
  long: null
  site_image_url: null
}

export function resetSiteFieldData(siteId: string): Promise<SiteFieldDataReset> {
  return api.post<SiteFieldDataReset>(`/sites/${siteId}/reset-field-data`, undefined, {
    showToast: false,
    suppressErrorToast: true,
  })
}

export interface SiteReactivated {
  id: string
  name?: string
  is_active: true
}

export function reactivateSite(siteId: string): Promise<SiteReactivated> {
  return api.post<SiteReactivated>(`/sites/${siteId}/reactivate`, undefined, {
    successMessage: 'Site reactivated.',
    suppressErrorToast: true,
  })
}
